import type { Draft, EditedKeypoint, EditMap, KeypointName, OutputJSON } from '../types';
import type { SecPoseAssetsResponse, SecPoseCamera } from './loadAssets';

export const ENCRYPTED_DRAFT_FORMAT = 'secpose-encrypted-draft' as const;
export const ENCRYPTED_DRAFT_VERSION = 1 as const;
export const ENCRYPTED_DRAFT_AAD = 'secpose-encrypted-draft-v1' as const;
export const PBKDF2_ITERATIONS = 600_000 as const;
export const MAX_ENCRYPTED_DRAFT_BYTES = 20 * 1024 * 1024;

const MAX_ELAPSED_MS = 365 * 24 * 60 * 60 * 1000;
const KEYPOINT_NAMES = new Set<KeypointName>([
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'pelvis',
]);
const VISIBILITIES = new Set<EditedKeypoint['visibility']>(['visible', 'occluded', 'out_of_frame']);
const SECPOSE_CAMERAS = new Set<SecPoseCamera>([
  'head_front_left',
  'head_front_right',
  'head_left',
  'head_right',
]);

export type DraftTransferErrorCode =
  | 'file_too_large'
  | 'unsupported_format'
  | 'invalid_crypto_parameters'
  | 'decrypt_failed'
  | 'episode_mismatch'
  | 'operator_mismatch'
  | 'source_mismatch'
  | 'invalid_draft'
  | 'lease_required'
  | 'not_editable'
  | 'persist_failed';

export class SecPoseDraftTransferError extends Error {
  readonly code: DraftTransferErrorCode;

  constructor(code: DraftTransferErrorCode, message: string) {
    super(message);
    this.name = 'SecPoseDraftTransferError';
    this.code = code;
  }
}

export interface EncryptedDraftContainerV1 {
  format: typeof ENCRYPTED_DRAFT_FORMAT;
  version: typeof ENCRYPTED_DRAFT_VERSION;
  crypto: {
    cipher: 'AES-256-GCM';
    kdf: 'PBKDF2-HMAC-SHA-256';
    iterations: typeof PBKDF2_ITERATIONS;
    salt_base64: string;
    iv_base64: string;
    aad: typeof ENCRYPTED_DRAFT_AAD;
    tag_length: 128;
  };
  ciphertext_base64: string;
}

export interface SecPoseDraftSource {
  frame_count: number;
  cameras: SecPoseCamera[];
  calibration_fingerprint: `sha256:${string}`;
  asset_fingerprint: `sha256:${string}`;
  /** Missing on pre-compatibility encrypted drafts and treated as blank. */
  pose_seed_source?: 'blank' | 'legacy_v1';
}

export interface SecPoseDraftExportV2 {
  format: 'secpose-draft-export-v2';
  flow: 'secpose';
  episode_id: string;
  operator: string;
  saved_at: string;
  exported_at: string;
  source: SecPoseDraftSource;
  draft: Draft;
}

interface LegacyDraftEntry {
  key?: unknown;
  episode_id: unknown;
  draft: unknown;
}

interface LegacyDraftExportV1 {
  format: 'secpose-draft-export-v1';
  drafts: LegacyDraftEntry[];
}

export type ParsedSecPoseDraftFile =
  | { kind: 'encrypted'; payload: SecPoseDraftExportV2 }
  | { kind: 'legacy'; payload: LegacyDraftExportV1 };

export interface SecPoseDraftImportContext {
  episodeId: string;
  operator: string;
  leaseId: string | null;
  editable: boolean;
  source: SecPoseDraftSource;
}

export interface SecPoseDraftImportCandidate {
  draft: Draft;
  legacy: boolean;
  source: SecPoseDraftSource | null;
  savedAt: string;
  elapsedMs: number;
  editedFrameCount: number;
  warnings: string[];
}

function fail(code: DraftTransferErrorCode, message: string): never {
  throw new SecPoseDraftTransferError(code, message);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isValidIsoDate(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && Number.isFinite(Date.parse(value));
}

function normalizeJson(value: unknown, seen = new WeakSet<object>()): unknown {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError('Cannot fingerprint a non-finite number');
    return Object.is(value, -0) ? 0 : value;
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) throw new TypeError('Cannot fingerprint a cyclic value');
    seen.add(value);
    const normalized = value.map((item) => normalizeJson(item, seen));
    seen.delete(value);
    return normalized;
  }
  if (isRecord(value)) {
    if (seen.has(value)) throw new TypeError('Cannot fingerprint a cyclic value');
    seen.add(value);
    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      const item = value[key];
      if (item === undefined || typeof item === 'function' || typeof item === 'symbol') continue;
      normalized[key] = normalizeJson(item, seen);
    }
    seen.delete(value);
    return normalized;
  }
  throw new TypeError('Cannot fingerprint unsupported data');
}

export function stableStringify(value: unknown): string {
  return JSON.stringify(normalizeJson(value));
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

export async function sha256Fingerprint(value: unknown): Promise<`sha256:${string}`> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(stableStringify(value))
  );
  return `sha256:${bytesToHex(new Uint8Array(digest))}`;
}

function sortedCameras(cameras: SecPoseCamera[]): SecPoseCamera[] {
  return [...new Set(cameras)].sort();
}

export async function buildSecPoseSourceDescriptor(
  assets: SecPoseAssetsResponse,
  poseSeedSource: 'blank' | 'legacy_v1' = 'blank'
): Promise<SecPoseDraftSource> {
  if (!Number.isInteger(assets.frame_count) || assets.frame_count <= 0) {
    fail('source_mismatch', 'SecPose source frame count is invalid.');
  }
  const cameras = sortedCameras(assets.cameras);
  const images = Object.fromEntries(
    cameras.map((camera) => [
      camera,
      [...(assets.images[camera] ?? [])]
        .map(({ frame_id, file_name, key }) => ({ frame_id, file_name, key }))
        .sort(
          (left, right) =>
            left.frame_id.localeCompare(right.frame_id) ||
            left.file_name.localeCompare(right.file_name) ||
            left.key.localeCompare(right.key)
        ),
    ])
  );
  const baseAssetIdentity: Record<string, unknown> = {
    source_key: assets.source_key,
    metadata: assets.metadata,
    frame_count: assets.frame_count,
    cameras,
    images,
  };
  if (poseSeedSource === 'legacy_v1') {
    baseAssetIdentity.pose_seed_source = poseSeedSource;
    baseAssetIdentity.pose_seed_toon = assets.estimations_toon;
  }
  const [calibrationFingerprint, assetFingerprint] = await Promise.all([
    sha256Fingerprint(assets.calibration),
    sha256Fingerprint(baseAssetIdentity),
  ]);
  return {
    frame_count: assets.frame_count,
    cameras,
    calibration_fingerprint: calibrationFingerprint,
    asset_fingerprint: assetFingerprint,
    pose_seed_source: poseSeedSource,
  };
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

function base64ToBytes(value: unknown, expectedLength?: number): Uint8Array<ArrayBuffer> {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length > MAX_ENCRYPTED_DRAFT_BYTES * 2
  ) {
    fail('invalid_crypto_parameters', 'Encrypted draft contains invalid Base64 data.');
  }
  if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)) {
    fail('invalid_crypto_parameters', 'Encrypted draft contains invalid Base64 data.');
  }
  let binary: string;
  try {
    binary = atob(value);
  } catch {
    fail('invalid_crypto_parameters', 'Encrypted draft contains invalid Base64 data.');
  }
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  if (expectedLength !== undefined && bytes.length !== expectedLength) {
    fail('invalid_crypto_parameters', 'Encrypted draft contains invalid crypto parameters.');
  }
  return bytes;
}

function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(new ArrayBuffer(length)));
}

export function generateRecoveryCode(): string {
  const raw = bytesToBase64(randomBytes(16))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  return raw.match(/.{1,4}/g)?.join('-') ?? raw;
}

async function deriveKey(recoveryCode: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  const normalizedCode = recoveryCode.trim();
  if (normalizedCode.length < 12 || normalizedCode.length > 256) {
    fail('decrypt_failed', 'Recovery code is incorrect or the file is damaged.');
  }
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(normalizedCode),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PBKDF2_ITERATIONS },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptSecPoseDraft(
  payload: SecPoseDraftExportV2,
  recoveryCode: string
): Promise<EncryptedDraftContainerV1> {
  validateV2Payload(payload);
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const key = await deriveKey(recoveryCode, salt);
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      additionalData: new TextEncoder().encode(ENCRYPTED_DRAFT_AAD),
      tagLength: 128,
    },
    key,
    new TextEncoder().encode(JSON.stringify(payload))
  );
  return {
    format: ENCRYPTED_DRAFT_FORMAT,
    version: ENCRYPTED_DRAFT_VERSION,
    crypto: {
      cipher: 'AES-256-GCM',
      kdf: 'PBKDF2-HMAC-SHA-256',
      iterations: PBKDF2_ITERATIONS,
      salt_base64: bytesToBase64(salt),
      iv_base64: bytesToBase64(iv),
      aad: ENCRYPTED_DRAFT_AAD,
      tag_length: 128,
    },
    ciphertext_base64: bytesToBase64(new Uint8Array(ciphertext)),
  };
}

function parseEncryptedContainer(value: unknown): EncryptedDraftContainerV1 {
  if (!isRecord(value) || value.format !== ENCRYPTED_DRAFT_FORMAT || value.version !== 1) {
    fail('unsupported_format', 'Unsupported SecPose draft format.');
  }
  const parameters = value.crypto;
  if (
    !isRecord(parameters) ||
    parameters.cipher !== 'AES-256-GCM' ||
    parameters.kdf !== 'PBKDF2-HMAC-SHA-256' ||
    parameters.iterations !== PBKDF2_ITERATIONS ||
    parameters.aad !== ENCRYPTED_DRAFT_AAD ||
    parameters.tag_length !== 128
  ) {
    fail('invalid_crypto_parameters', 'Encrypted draft contains invalid crypto parameters.');
  }
  const salt = base64ToBytes(parameters.salt_base64, 16);
  const iv = base64ToBytes(parameters.iv_base64, 12);
  const ciphertext = base64ToBytes(value.ciphertext_base64);
  if (ciphertext.length <= 16 || ciphertext.length > MAX_ENCRYPTED_DRAFT_BYTES) {
    fail('invalid_crypto_parameters', 'Encrypted draft ciphertext size is invalid.');
  }
  return {
    format: ENCRYPTED_DRAFT_FORMAT,
    version: ENCRYPTED_DRAFT_VERSION,
    crypto: {
      cipher: 'AES-256-GCM',
      kdf: 'PBKDF2-HMAC-SHA-256',
      iterations: PBKDF2_ITERATIONS,
      salt_base64: bytesToBase64(salt),
      iv_base64: bytesToBase64(iv),
      aad: ENCRYPTED_DRAFT_AAD,
      tag_length: 128,
    },
    ciphertext_base64: bytesToBase64(ciphertext),
  };
}

export async function decryptSecPoseDraft(
  containerValue: unknown,
  recoveryCode: string
): Promise<SecPoseDraftExportV2> {
  const container = parseEncryptedContainer(containerValue);
  const salt = base64ToBytes(container.crypto.salt_base64, 16);
  const iv = base64ToBytes(container.crypto.iv_base64, 12);
  const ciphertext = base64ToBytes(container.ciphertext_base64);
  try {
    const key = await deriveKey(recoveryCode, salt);
    const plaintext = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
        additionalData: new TextEncoder().encode(ENCRYPTED_DRAFT_AAD),
        tagLength: 128,
      },
      key,
      ciphertext
    );
    const payload = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(plaintext));
    validateV2Payload(payload);
    return payload;
  } catch (error) {
    if (error instanceof SecPoseDraftTransferError && error.code === 'invalid_draft') throw error;
    fail('decrypt_failed', 'Recovery code is incorrect or the file is damaged.');
  }
}

function validateEditedKeypoint(value: unknown): value is EditedKeypoint {
  if (!isRecord(value)) return false;
  const xy = value.xy;
  if (
    !Array.isArray(xy) ||
    xy.length !== 2 ||
    !isFiniteNumber(xy[0]) ||
    !isFiniteNumber(xy[1]) ||
    Math.abs(xy[0]) > 1_000_000 ||
    Math.abs(xy[1]) > 1_000_000 ||
    !VISIBILITIES.has(value.visibility as EditedKeypoint['visibility']) ||
    typeof value.locked !== 'boolean' ||
    (value.seeded !== undefined && typeof value.seeded !== 'boolean')
  ) {
    return false;
  }
  if (value.visibility === 'out_of_frame' && (xy[0] !== -1 || xy[1] !== -1)) return false;
  return true;
}

function validateEditMap(value: unknown, frameCount: number): value is EditMap {
  if (!isRecord(value)) return false;
  for (const [frameKey, frameEdits] of Object.entries(value)) {
    const frame = Number(frameKey);
    if (!Number.isInteger(frame) || frame < 0 || frame >= frameCount || !isRecord(frameEdits)) {
      return false;
    }
    for (const [keypointName, edit] of Object.entries(frameEdits)) {
      if (!KEYPOINT_NAMES.has(keypointName as KeypointName) || !validateEditedKeypoint(edit)) {
        return false;
      }
    }
  }
  return true;
}

function validateOutput(value: unknown, frameCount: number): value is OutputJSON {
  if (!isRecord(value)) return false;
  const imageSize = value.image_size;
  return (
    typeof value.video === 'string' &&
    Array.isArray(imageSize) &&
    imageSize.length === 2 &&
    isFiniteNumber(imageSize[0]) &&
    imageSize[0] > 0 &&
    isFiniteNumber(imageSize[1]) &&
    imageSize[1] > 0 &&
    isFiniteNumber(value.fps) &&
    value.fps > 0 &&
    Number.isInteger(value.num_frames) &&
    value.num_frames === frameCount &&
    value.schema_version === 1 &&
    validateEditMap(value.edits, frameCount)
  );
}

export function validateDraft(value: unknown, frameCount: number): asserts value is Draft {
  if (
    !isRecord(value) ||
    !validateOutput(value.front_left, frameCount) ||
    !validateOutput(value.front_right, frameCount)
  ) {
    fail('invalid_draft', 'SecPose draft structure is invalid.');
  }
  if (value.left !== undefined && !validateOutput(value.left, frameCount)) {
    fail('invalid_draft', 'SecPose draft left camera data is invalid.');
  }
  if (value.right !== undefined && !validateOutput(value.right, frameCount)) {
    fail('invalid_draft', 'SecPose draft right camera data is invalid.');
  }
  if (
    value.audited !== undefined &&
    (!Array.isArray(value.audited) ||
      value.audited.some(
        (frame) =>
          !Number.isInteger(frame) || (frame as number) < 0 || (frame as number) >= frameCount
      ))
  ) {
    fail('invalid_draft', 'SecPose draft audit progress is invalid.');
  }
  if (
    value.elapsedMs !== undefined &&
    (!isFiniteNumber(value.elapsedMs) || value.elapsedMs < 0 || value.elapsedMs > MAX_ELAPSED_MS)
  ) {
    fail('invalid_draft', 'SecPose draft elapsed time is invalid.');
  }
  if (
    value.poseSeedSource !== undefined &&
    value.poseSeedSource !== 'blank' &&
    value.poseSeedSource !== 'legacy_v1'
  ) {
    fail('invalid_draft', 'SecPose draft pose seed source is invalid.');
  }
  if (!isValidIsoDate(value.savedAt)) {
    fail('invalid_draft', 'SecPose draft saved time is invalid.');
  }
}

function isFingerprint(value: unknown): value is `sha256:${string}` {
  return typeof value === 'string' && /^sha256:[a-f0-9]{64}$/.test(value);
}

function validateSource(value: unknown): asserts value is SecPoseDraftSource {
  if (
    !isRecord(value) ||
    !Number.isInteger(value.frame_count) ||
    (value.frame_count as number) <= 0 ||
    !Array.isArray(value.cameras) ||
    value.cameras.length === 0 ||
    value.cameras.some((camera) => !SECPOSE_CAMERAS.has(camera as SecPoseCamera)) ||
    new Set(value.cameras).size !== value.cameras.length ||
    !isFingerprint(value.calibration_fingerprint) ||
    !isFingerprint(value.asset_fingerprint)
  ) {
    fail('invalid_draft', 'SecPose draft source descriptor is invalid.');
  }
  if (
    value.pose_seed_source !== undefined &&
    value.pose_seed_source !== 'blank' &&
    value.pose_seed_source !== 'legacy_v1'
  ) {
    fail('invalid_draft', 'SecPose draft source descriptor is invalid.');
  }
}

function validateV2Payload(value: unknown): asserts value is SecPoseDraftExportV2 {
  if (
    !isRecord(value) ||
    value.format !== 'secpose-draft-export-v2' ||
    value.flow !== 'secpose' ||
    typeof value.episode_id !== 'string' ||
    value.episode_id.length === 0 ||
    typeof value.operator !== 'string' ||
    value.operator.length === 0 ||
    !isValidIsoDate(value.saved_at) ||
    !isValidIsoDate(value.exported_at)
  ) {
    fail('invalid_draft', 'SecPose encrypted draft payload is invalid.');
  }
  validateSource(value.source);
  validateDraft(value.draft, value.source.frame_count);
  if ((value.draft.poseSeedSource ?? 'blank') !== (value.source.pose_seed_source ?? 'blank')) {
    fail('invalid_draft', 'SecPose draft pose seed does not match its source descriptor.');
  }
  if (value.saved_at !== value.draft.savedAt) {
    fail('invalid_draft', 'SecPose draft saved time does not match its payload.');
  }
}

function parseJson(text: string): unknown {
  if (new Blob([text]).size > MAX_ENCRYPTED_DRAFT_BYTES) {
    fail('file_too_large', 'SecPose draft file exceeds 20 MiB.');
  }
  try {
    return JSON.parse(text);
  } catch {
    fail('unsupported_format', 'SecPose draft file is not valid JSON.');
  }
}

export async function parseSecPoseDraftFile(
  text: string,
  recoveryCode?: string
): Promise<ParsedSecPoseDraftFile> {
  const value = parseJson(text);
  if (isRecord(value) && value.format === ENCRYPTED_DRAFT_FORMAT) {
    if (!recoveryCode) fail('decrypt_failed', 'Recovery code is required.');
    return { kind: 'encrypted', payload: await decryptSecPoseDraft(value, recoveryCode) };
  }
  if (
    isRecord(value) &&
    value.format === 'secpose-draft-export-v1' &&
    Array.isArray(value.drafts)
  ) {
    return { kind: 'legacy', payload: value as unknown as LegacyDraftExportV1 };
  }
  fail('unsupported_format', 'Unsupported SecPose draft format.');
}

export function inspectSecPoseDraftFile(text: string): ParsedSecPoseDraftFile['kind'] {
  const value = parseJson(text);
  if (isRecord(value) && value.format === ENCRYPTED_DRAFT_FORMAT) {
    parseEncryptedContainer(value);
    return 'encrypted';
  }
  if (
    isRecord(value) &&
    value.format === 'secpose-draft-export-v1' &&
    Array.isArray(value.drafts)
  ) {
    return 'legacy';
  }
  fail('unsupported_format', 'Unsupported SecPose draft format.');
}

function sourceMatches(left: SecPoseDraftSource, right: SecPoseDraftSource): boolean {
  return (
    left.frame_count === right.frame_count &&
    stableStringify(sortedCameras(left.cameras)) ===
      stableStringify(sortedCameras(right.cameras)) &&
    left.calibration_fingerprint === right.calibration_fingerprint &&
    left.asset_fingerprint === right.asset_fingerprint &&
    (left.pose_seed_source ?? 'blank') === (right.pose_seed_source ?? 'blank')
  );
}

export function countDraftEditedFrames(draft: Draft): number {
  const frames = new Set<number>();
  for (const output of [draft.front_left, draft.front_right, draft.left, draft.right]) {
    if (!output) continue;
    for (const [frameKey, edits] of Object.entries(output.edits)) {
      if (Object.keys(edits ?? {}).length > 0) frames.add(Number(frameKey));
    }
  }
  return frames.size;
}

export function validateSecPoseDraftImport(
  parsed: ParsedSecPoseDraftFile,
  context: SecPoseDraftImportContext
): SecPoseDraftImportCandidate {
  if (!context.editable)
    fail('not_editable', 'Draft recovery is available only in editable annotation mode.');
  if (!context.leaseId?.trim())
    fail('lease_required', 'Claim this Episode before restoring a draft.');

  let draft: Draft;
  let legacy = false;
  let importedSource: SecPoseDraftSource | null = null;
  const warnings: string[] = [];
  if (parsed.kind === 'encrypted') {
    const payload = parsed.payload;
    if (payload.episode_id !== context.episodeId) {
      fail('episode_mismatch', 'Draft Episode does not match the currently claimed Episode.');
    }
    if (payload.operator !== context.operator) {
      fail('operator_mismatch', 'Draft operator does not match the current operator.');
    }
    if (!sourceMatches(payload.source, context.source)) {
      fail('source_mismatch', 'Draft source data does not match the loaded SecPose assets.');
    }
    draft = payload.draft;
    importedSource = payload.source;
  } else {
    const matches = parsed.payload.drafts.filter(
      (entry) => isRecord(entry) && entry.episode_id === context.episodeId
    );
    if (matches.length !== 1) {
      fail('episode_mismatch', 'Legacy draft must contain exactly one matching Episode.');
    }
    draft = matches[0]!.draft as Draft;
    legacy = true;
    warnings.push(
      'Legacy unencrypted draft: operator identity and source fingerprints cannot be verified.'
    );
  }
  validateDraft(draft, context.source.frame_count);
  const draftPoseSeedSource = draft.poseSeedSource ?? 'blank';
  const currentPoseSeedSource = context.source.pose_seed_source ?? 'blank';
  if (draftPoseSeedSource !== currentPoseSeedSource) {
    fail('source_mismatch', 'Draft pose seed does not match the loaded SecPose assets.');
  }
  const acceptedDraft = structuredClone(draft);
  return {
    draft: acceptedDraft,
    legacy,
    source: importedSource,
    savedAt: acceptedDraft.savedAt,
    elapsedMs: acceptedDraft.elapsedMs ?? 0,
    editedFrameCount: countDraftEditedFrames(acceptedDraft),
    warnings,
  };
}

export function buildSecPoseDraftExport(input: {
  episodeId: string;
  operator: string;
  source: SecPoseDraftSource;
  draft: Draft;
  exportedAt?: string;
}): SecPoseDraftExportV2 {
  const payload: SecPoseDraftExportV2 = {
    format: 'secpose-draft-export-v2',
    flow: 'secpose',
    episode_id: input.episodeId,
    operator: input.operator,
    saved_at: input.draft.savedAt,
    exported_at: input.exportedAt ?? new Date().toISOString(),
    source: input.source,
    draft: input.draft,
  };
  validateV2Payload(payload);
  return payload;
}
